/**
 * Contrato de las secciones de configuración de un tenant.
 *
 * La misma sección se monta en dos contenedores:
 *  - wizard  (`/tenants/new`)  → secuencia con stepper, footer "Guardar y continuar →"
 *  - edición (`/tenants/:id`)  → tabs, footer "Guardar sección"
 *
 * La sección SIEMPRE se guarda a sí misma llamando al endpoint que le
 * corresponde; el contenedor solo reacciona a `onSaved` (en wizard, avanza).
 */
export type SectionMode = 'wizard' | 'edit';

/**
 * Estado de configuración de una sección. Se muestra como chip en la sección y
 * como punto de color en el stepper / las tabs.
 *
 * `unknown` existe porque la API todavía no expone la config guardada de
 * algunas secciones (ERP, modo del bot): decir "Sin configurar" ahí sería
 * mentirle al operador.
 */
export type SectionStatus = 'complete' | 'partial' | 'empty' | 'unknown';

export interface TenantSectionProps<TValue = unknown> {
  mode: SectionMode;
  tenantId: string;
  /** Config actual del tenant (en 'edit') o el draft (en 'wizard'). */
  value: TValue;
  /** Se llama tras guardar OK. En wizard el contenedor avanza de paso. */
  onSaved?: (patch: unknown) => void;
  /** data-testid del contenedor de la sección (lo fija quien la monta). */
  testId?: string;
}

export const STATUS_LABEL: Record<SectionStatus, string> = {
  complete: 'Completo',
  partial: 'Falta algo',
  empty: 'Sin configurar',
  unknown: 'Sin verificar',
};

export const STATUS_CHIP_CLASS: Record<SectionStatus, string> = {
  complete: 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400',
  partial: 'bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-400',
  empty:
    'bg-neutral-100 text-neutral-500 border border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700',
  unknown:
    'bg-neutral-100 text-neutral-500 border border-neutral-200 dark:bg-neutral-800 dark:text-neutral-400 dark:border-neutral-700',
};

export const STATUS_DOT_CLASS: Record<SectionStatus, string> = {
  complete: 'bg-success-600 dark:bg-success-400',
  partial: 'bg-warning-600 dark:bg-warning-400',
  empty: 'bg-neutral-300 dark:bg-neutral-600',
  unknown: 'bg-neutral-300 dark:bg-neutral-600',
};
